<template>
  <span>
    <v-row>
      <v-spacer/>
      <v-col align="right">
        <v-menu
        v-model="groupingConfigMenu"
        :close-on-content-click="false"
        left>
          <template v-slot:activator="{on: menu, attrs}">
            <v-tooltip bottom color="primary">
              <template #activator="{on: tooltip}">
                <v-icon color="primary" class="my-2 mr-2" v-bind="attrs" v-on="{...tooltip, ...menu}">mdi-filter-cog</v-icon>
              </template>
              <span class="subtitle-1 white--text" style="white-space: pre-line">
                {{ $t('selectGroup') }}
              </span>
            </v-tooltip>
          </template>
          <v-card>
            <v-card-title class="primary white--text">
              {{ $t('selectGroup') }}
            </v-card-title>
            <v-card-text class="px-0" fluid>
              <v-container>
                <v-radio-group v-model="radioSelection">
                  <v-radio
                  v-for="(header, i) in headers"
                  :key="i"
                  color="primary"
                  :value="header"
                  :label="header.text"
                  @click="groupingConfigMenu = false"/>
                </v-radio-group>
              </v-container>
            </v-card-text>
          </v-card>
        </v-menu>
      </v-col>
    </v-row>
    <v-expansion-panels accordion tile v-model="openPanels" class="narrow-expansion-panels" :key="expansionPanelKey">
      <v-expansion-panel v-for="(groupItem, i) in groupedItems" :key="i">
        <v-expansion-panel-header>
          <span class="subtitle-1" :style="(groupItem.group === 'Base Rate' && radioSelection.value === 'product') ? 'font-style: italic; font-weight: bold;' : ''">
            {{ panelTitle(groupItem.group, radioSelection.value) }}
            <Icon
              v-if="radioSelection.value === 'counterparty' && typeof groupItem.group === 'number'"
              icon="mdi-alpha-r-circle-outline"
              :small="false"
              icon-color="info"
              :tooltipText="runtimeCounterpartyTooltipForGroup(groupItem)"
              class="ml-1"
            />
          </span>
          <template #actions>
            <span v-if="showRateGroupedColumn" class="mr-2 mt-1">
              <Icon
                v-if="groupItem.rates.length > 1"
                icon="mdi-dots-horizontal"
                icon-color="black"
                :tooltipText="$t('multipleDistinctRates')"/>
              <span v-else>
                {{ groupItem.rates[0] ? formatMoney(groupItem.rates[0]) : formatMoney(0) }}
              </span>
            </span>
            <v-icon>mdi-chevron-down</v-icon>
          </template>
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <v-data-table
            :headers="tableHeaders"
            :items="groupItem.items"
            dense
            :show-expand="expandToContract && !hideContractCards"
            single-expand
            :style="`cursor: ${expandToContract && !hideContractCards ? 'pointer' : 'default'}`"
            :expanded="expanded"
            @click:row="rowClicked"
            item-key="_key"
            :items-per-page="-1"
            hide-default-footer
          >
            <template #item.rate="{item}">
              {{ formatMoney(item.rate) }}
            </template>
            <template #item.counterparty="{ item }">
              <Icon
                v-if="item.isRcp"
                icon="mdi-alpha-r-circle-outline"
                :small="false"
                icon-color="info"
                :tooltipText="runtimeCounterpartyTooltip(item)"
              />
              <span>{{item.isRcp ? $t(RuntimeCounterparty.labelFromInt(item.counterparty)) : item.counterparty}}</span>
            </template>
            <template #item.product="{ item }">
              <span :style="item.product === 'Base Rate' ? 'font-style: italic; font-weight: bold;' : ''">
                {{ item.product }}
              </span>
            </template>
            <template #expanded-item="{ headers, item }">
              <td class="grey lighten-5" :colspan="headers.length+1">
                <v-row>
                  <v-col cols="12" md="6" xl="4" v-for="(contract, idx) of getContracts(item)" :key="idx">
                    <RateVerificationContractCard
                      :contract="contract"
                      :hideEditAndUnpostActions="hideEditAndUnpostActions"
                      @unposted-tickets="unpostedTickets"
                      @contract-edited="$emit('contract-edited')"
                    />
                  </v-col>
                </v-row>
              </td>
            </template>
          </v-data-table>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>
  </span>
</template>

<script>
import { formatMoney } from '@/utils/NumericMutations.js'
import { RuntimeCounterparty } from '@/utils/Enumerations'
import { mapActions } from 'vuex'

export default {
  name: 'RateVerificationTable',

  components: {
    RateVerificationContractCard: () => import('./RateVerificationContractCard.vue'),
    Icon: () => import('@/components/helper/Icon.vue')
  },

  props: {
    items: {
      type: Array,
      required: true
    },
    groupingKeys: {
      type: Array,
      required: true
    },
    expandToContract: {
      type: Boolean,
      default: true
    },
    hideEditAndUnpostActions: {
      type: Boolean,
      default: false
    },
    tractId: {
      type: Number,
      required: false
    },
    expansionPanelKey: {
      type: String,
      required: false
    },
    hideContractCards: {
      type: Boolean,
      default: false
    }
  },

  data: () => ({
    headers: [],
    expanded: [],
    groupingConfigMenu: false,
    radioSelection: undefined,
    openPanels: null,
    tract: null,
    RuntimeCounterparty
  }),

  watch: {
    hideContractCards (val) {
      if (val) {
        this.expanded = []
      }
    },
    radioSelection () {
      this.openPanels = null
      this.expanded = []
    },
    openPanels () {
      this.expanded = []
    }
  },

  computed: {
    groupedItems () {
      if (!this.radioSelection) return []
      const key = this.radioSelection.value
      const firstCol = this.headers.filter(h => h.value !== key)[0]?.value

      const grouped = Object.groupBy(this.items, item => item[key])
      const groups = Object.entries(grouped).map(([group, items]) => ({
        group: isNaN(group) ? group : Number(group),
        items: firstCol ? [...items].sort((a, b) => this.itemSortValue(a, firstCol).localeCompare(this.itemSortValue(b, firstCol))) : items,
        rates: [...new Set(items.map(i => i.rate))]
      }))

      if (key === 'rate') {
        groups.sort((a, b) => (a.group || 0) - (b.group || 0))
      } else {
        groups.sort((a, b) => this.groupSortLabel(a.group, key).localeCompare(this.groupSortLabel(b.group, key)))
      }

      return groups
    },

    tableHeaders () {
      if (!this.radioSelection) return this.headers
      return this.headers.filter(h => h.value !== this.radioSelection.value)
    },

    showRateGroupedColumn () {
      return this.radioSelection.value !== 'rate'
    }
  },

  created () {
    this.headers = this.groupingKeys
    this.radioSelection = this.headers[0]
    if (this.tractId) {
      this.fetchTractData()
    }
  },

  methods: {
    ...mapActions('tract', ['fetchTract']),
    formatMoney,

    async fetchTractData () {
      this.tract = await this.fetchTract(this.tractId)
    },

    counterpartyNameHelper (counterpartyValue, emptyFallback) {
      if (!this.tract) return emptyFallback
      switch (counterpartyValue) {
        case RuntimeCounterparty.Landowner.value:
          return this.tract.landOwnerAccountName || this.$t('nLandowners', { n: this.tract.landownerCount }) || emptyFallback
        case RuntimeCounterparty.DefaultLogger.value:
          return this.tract.loggerAccountName || emptyFallback
        case RuntimeCounterparty.ContractAccount.value:
          return this.tract.contractAccount || emptyFallback
        case RuntimeCounterparty.Hauler.value:
          return this.tract.haulerAccount || emptyFallback
        case RuntimeCounterparty.Supplier.value:
          return this.tract.supplierAccount || emptyFallback
        case RuntimeCounterparty.ConsultingForester.value:
          return this.tract.consultingForesterAccount || emptyFallback
        case RuntimeCounterparty.Driver.value:
          return this.$t('ticketDriver')
        default:
          return emptyFallback
      }
    },

    runtimeCounterpartyTooltip (item) {
      if (!item.isRcp || !this.tract) return this.$t('notApplicable')
      if (item.counterparty === RuntimeCounterparty.DestinationAccount.value) {
        return this.destinationAccountTooltip(item.contracts)
      }
      return this.counterpartyNameHelper(item.counterparty, this.$t('notSpecified'))
    },

    runtimeCounterpartyTooltipForGroup (groupItem) {
      if (groupItem.group === RuntimeCounterparty.DestinationAccount.value) {
        const allContracts = groupItem.items.flatMap(item => item.contracts || [])
        return this.destinationAccountTooltip(allContracts)
      }
      return this.counterpartyNameHelper(groupItem.group, this.$t('notSpecified'))
    },

    destinationAccountTooltip (contracts) {
      if (!contracts || contracts.length === 0) return this.$t('notSpecified')
      const uniqueDestinations = [...new Set(contracts.map(c => c.destinationName).filter(Boolean))]
      if (uniqueDestinations.length === 1) return uniqueDestinations[0]
      if (uniqueDestinations.length > 1) return this.$t('multipleAccounts')
      return this.$t('notSpecified')
    },

    rowClicked (item) {
      if (!this.expandToContract || this.hideContractCards) return
      this.$nextTick(() => {
        this.expanded = (this.expanded.includes(item)) ? [] : [item]
      })
    },

    panelTitle (groupValue, groupKey) {
      const groupLabel = this.$t(groupKey)
      let value

      if (groupKey === 'counterparty' && typeof groupValue === 'number') {
        value = this.$t(`${RuntimeCounterparty.labelFromInt(groupValue)}`)
      } else if (groupKey === 'product') {
        value = groupValue && groupValue.length > 0 ? groupValue.split(', ')[0] : ''
      } else if (groupKey === 'rate') {
        value = formatMoney(groupValue)
      } else {
        value = groupValue
      }

      return `${groupLabel}: ${value}`
    },

    getContracts (item) {
      return item.contracts
    },

    unpostedTickets (contractId) {
      this.expanded[0].contracts = this.expanded[0].contracts.filter(c => c.contractId !== contractId)
      if (this.expanded[0].contracts.length === 0) this.expanded = []
      this.$emit('refresh-batch')
    },

    itemSortValue (item, columnKey) {
      if (columnKey === 'counterparty' && item.isRcp) {
        return this.$t(RuntimeCounterparty.labelFromInt(item.counterparty))
      }
      return String(item[columnKey] ?? '')
    },

    groupSortLabel (groupValue, groupKey) {
      if (groupKey === 'counterparty' && typeof groupValue === 'number') {
        return this.$t(RuntimeCounterparty.labelFromInt(groupValue))
      }
      return String(groupValue ?? '')
    }
  }
}
</script>
